<script lang="ts">
    import { onMount, getContext, tick } from 'svelte';
    import FutureStartNote from "./FutureStartNote.svelte";
    import { LocalDate } from "@/time";
    import { initForecastInputs } from "@/Util/api";
    import { getCurrentFitnessSignature, getCurrentTrainingLoads, round, specificityToRating, startDateFromInput, type XSSDetails } from '@/Util/utils';
    import { 
        ProgramType, 
        defaultGoalPlan,
        getFocus,
    } from "@/Common/constants";
    import type { 
        CalendarDefaults,
        ProgramFocusTypes,
        XfaiFormProps,
    } from "@/Common/constants";
    import { DatePicker, SelectSlider, InfoIcon } from "@/Common";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";
    import {ProgramAchievabilityMessage, ProgramFitnessSignature, ProgramTrainingLoad, FocusPowerIncrease, FrontierLabel} from './ProgramElements';
    import { createProgramAchievability } from './modules/ProgramAchievability';
    import { createForecastInputs, type GoalForecastTargets, assessGoal, assessManualGoal, estimateTargets, type GoalStatus, type ManualGoalAssessment } from './modules/ForecastFunctions';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import type { MedianAndWeatherStores, MyFitnessStores } from '@/MyFitness/stores';
    import { writable } from 'svelte/store';
    import {WeeklyHoursSlider} from './ProgramElements/SetupFormInputs';
    import FocusPicker from './ProgramElements/SetupFormInputs/FocusPicker.svelte';
    import FrontierEnds from './ProgramElements/SetupFormInputs/FrontierEnds.svelte';
    import XfaiGeneralSettings from './ProgramElements/XfaiGeneralSettings.svelte';
    import WeekAvailability from '@/SetupWizards/WeekAvailability.svelte';
    import FrontierAdjustDialog from '@/SetupWizards/FrontierAdjustDialog.svelte';
    import { addDays, differenceInCalendarDays, format } from 'date-fns';
    import { getLibxert } from '@/xert-wasm';
    import * as RadioGroup from "@/lib/components/ui/radio-group";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { slide } from 'svelte/transition';
    import type { GoalTargetsMode } from "@/Common/constants";
    import type { ManualSignatureTargets } from './modules/ForecastFunctions';
    import { signed } from '@/PlanReport/format';

    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { userParams, trainingAdvice } = myFitness
    const { trainingMedians } = getContext<MedianAndWeatherStores>('medianAndWeather')
    let currentSignature: XSSDetails
    let currentTL: XSSDetails

    function reactiveUpdate() {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
        // focusIncrease's initial value (above) is seeded from $currentFocusPower before $trainingAdvice may have
        // loaded, so it can start out negative or huge; re-clamp here, on every advice update, the same way the
        // date/focus handlers below already do, so the displayed Power Increase is never out of range.
        focusIncrease = Math.max(min_focusIncrease, Math.min(focusIncrease, focusIncreaseLimit(frontier, max_focusIncrease, isManual())))
        $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
        updateInitialInputs()
    }
    $: if($trainingAdvice && !disabled) reactiveUpdate()

    let athleteTypes = Object.keys($userParams.athletes) as ProgramFocusTypes[]
    const athleteDurations = Object.fromEntries(athleteTypes.map(a => [a, $userParams.athletes[a].duration])) as Record<string, number>
    
    type $$Props = XfaiFormProps<ProgramType.Goal>
    export let plan = writable({...defaultGoalPlan})
    export let forecastInputs: any
    export let disabled: boolean
    // Updating the existing plan can't move its start date, so the field is hidden then.
    export let hideStartDate = false
    export let achievabilityId = "goal-achievability"
    export const forecastReady = true
    export const refresh = updateInitialInputs
    let className = ""
    export {className as class}

    /* Date input targetDate */
    let targetDate: string = LocalDate.format($plan.targetDate)
    let startDate: string = LocalDate.format($plan.startDate ?? new Date())

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const {currentFocusPower, focusPowerDuration} = focusPower
    focusPower.updateFocusPower($plan.targetAthlete)
    
    /* Focus Power percent to Focus Power*/
    let focusIncrease: number = $plan.focusPower ? Math.round($plan.focusPower - $currentFocusPower) : 1
    // A plan without an increase (defaultGoalPlan's placeholder focus power) starts at the fine share of the frontier once it
    // is known, unless the increase was changed first
    let increaseFromFrontier = !$plan.focusPower || $plan.focusPower <= defaultGoalPlan.focusPower
    const min_focusIncrease = 1
    const getMaxFocusIncrease = () => Math.max(20, Math.round(differenceInCalendarDays($plan.targetDate, new Date())/7.0*6))
    let max_focusIncrease = getMaxFocusIncrease()

    // The estimated frontier (assessGoal), in watts, for a goal by focus. It depends on the dates, focus, specificity,
    // settings and availability, not on the power increase chosen. The slider ends at it (Rigid: a plan that needs every
    // workout), or without one at the most a plan of this length is allowed.
    let frontier: number | null = null
    // The goal's focus power the frontier was last assessed with, and the earliest date that assessment found
    let assessedFocusPower: number | null = null
    let earliestDate: Date | null = null
    let frontierKey = ""
    let frontierRequest = 0
    // The most the power increase can be, for the slider and the clamps when the advice or the end date changes: for a goal
    // by focus once the frontier is known, the frontier. Otherwise the most a plan of this length is allowed.
    function focusIncreaseLimit(frontier: number | null, maxForLength: number, manual: boolean) {
        if (frontier == null || manual) return maxForLength
        return Math.max(min_focusIncrease + 1, Math.round(frontier))
    }
    $: sliderMax = focusIncreaseLimit(frontier, max_focusIncrease, isManual())
    $: frontierWatts = frontier != null && $plan.targetsMode !== "manual" ? Math.round(frontier) : null
    // The last tenth before the frontier is yellow: up to it the increase is fine (fineShare in assessCapacity)
    $: fineWatts = frontier != null ? Math.floor(0.9*frontier) : null
    // A frontier with no room for an increase (the days can't raise the focus power, or lose some of it): the slider is past
    // it all the way, in red
    $: noRoom = frontierWatts != null && frontierWatts <= min_focusIncrease
    // A plan being viewed, not set up, keeps its increase
    $: if (focusIncrease > sliderMax && !isManual() && !disabled) {
        focusIncrease = sliderMax
        $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
        updateGoalTargets()
    }
    // Manual signature targets against the plan's training capacity too (assessManualGoal): each signature value's status
    let manualAssessment: ManualGoalAssessment | null = null
    async function updateFrontier(inputs: any) {
        if (!inputs || (isManual() && !$plan.manualSignature)) return
        const manual = isManual()
        const key = JSON.stringify([manual, manual ? $plan.manualSignature : $plan.focusPower, targetDate, $plan.startDate, $plan.targetAthlete, $plan.specificity, $plan.settings, $plan.cal_settings, inputs.t0])
        if (key === frontierKey) return
        frontierKey = key
        const request = ++frontierRequest
        if (manual) {
            const assessment = await assessManualGoal(inputs, $plan.cal_settings, $trainingMedians, "goal")
            if (request !== frontierRequest) return
            manualAssessment = assessment
            if (targetTL) validateTargets()
            return
        }
        const focusPower = $plan.focusPower
        const assessment = await assessGoal(inputs, $plan.cal_settings, $trainingMedians, "goal")
        if (request !== frontierRequest) return
        frontier = assessment ? assessment.frontierFocusPower - assessment.currentFocusPower : null
        assessedFocusPower = focusPower
        earliestDate = assessment?.earliestDate ?? null
        if (increaseFromFrontier && frontier != null && !disabled) {
            increaseFromFrontier = false
            focusIncrease = Math.max(min_focusIncrease, Math.floor(0.9*frontier))
            $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
            updateGoalTargets()
            return
        }
        if (targetTL) validateTargets()
    }

    /* Targets */
    let targetSignature: XSSDetails
    let targetTL: XSSDetails

    /* Manual Signature Targets (HIE is entered in kJ, stored in J) */
    let manualFtp: number | undefined = $plan.manualSignature?.ftp
    let manualHie: number | undefined = $plan.manualSignature ? round($plan.manualSignature.hie / 1000, 1) : undefined
    let manualPp: number | undefined = $plan.manualSignature?.pp
    const isManual = () => $plan.targetsMode === "manual"

    /* Lowest reachable signature (no training until the end date), in input units */
    type SignatureField = "ftp" | "hie" | "pp"
    let minSignature: ManualSignatureTargets | null = null
    const getMinValues = (min: ManualSignatureTargets | null) => ({
        ftp: min ? Math.ceil(min.ftp) : 1,
        hie: min ? Math.ceil(min.hie / 100) / 10 : 0.1,
        pp: min ? Math.ceil(min.pp) : 1,
    })
    $: minValues = getMinValues(minSignature)

    // Briefly explain why a value was raised to its minimum
    let minNotes: Record<SignatureField, boolean> = { ftp: false, hie: false, pp: false }
    const minNoteTimers: Partial<Record<SignatureField, ReturnType<typeof setTimeout>>> = {}
    function flashMinNote(field: SignatureField) {
        minNotes[field] = true
        clearTimeout(minNoteTimers[field])
        minNoteTimers[field] = setTimeout(() => minNotes[field] = false, 5000)
    }

    /** Raise manual values below the lowest reachable signature. Returns true if any changed. */
    function clampToMinimums() {
        const min = getMinValues(minSignature)
        let clamped = false
        if (manualFtp !== undefined && manualFtp < min.ftp) { manualFtp = min.ftp; flashMinNote("ftp"); clamped = true }
        if (manualHie !== undefined && manualHie < min.hie) { manualHie = min.hie; flashMinNote("hie"); clamped = true }
        if (manualPp !== undefined && manualPp < min.pp) { manualPp = min.pp; flashMinNote("pp"); clamped = true }
        return clamped
    }

    // Manual targets have no focus: use the focus and specificity of the current training loads.
    // Keep the By Focus choices to restore them when switching back.
    let focusBeforeManual: { targetAthlete: ProgramFocusTypes, specificity: typeof $plan.specificity } | null = null
    async function setFocusFromTrainingLoads() {
        const libxert = await getLibxert()
        const sig = $trainingAdvice.signature
        const tl = getCurrentTrainingLoads($trainingAdvice)
        const sfd = libxert.get_focus_duration(sig.pp, sig.ftp, sig.atc, tl.xlss, tl.xhss, tl.xpss)
        const sp = libxert.get_specificity(sig.pp, sig.ftp, sig.atc, tl.xlss, tl.xhss, tl.xpss)
        $plan.targetAthlete = getFocus($userParams.athletes, sfd)
        $plan.specificity = specificityToRating(sp)
        focusPower.updateFocusPower($plan.targetAthlete)
    }

    function onTargetsModeValueChange(value: string | undefined) {
        $plan.targetsMode = value as GoalTargetsMode
        onTargetsModeChange()
    }

    function onTargetsModeChange() {
        if (isManual()) {
            focusBeforeManual = { targetAthlete: $plan.targetAthlete, specificity: $plan.specificity }
        } else if (focusBeforeManual) {
            $plan.targetAthlete = focusBeforeManual.targetAthlete
            $plan.specificity = focusBeforeManual.specificity
            focusBeforeManual = null
        }
        if (isManual() && !$plan.manualSignature) {
            // Start from the targets estimated by focus, or the current signature
            const sig = targetSignature ?? currentSignature
            if (sig) {
                manualFtp = Math.round(sig.xlss)
                manualHie = round(sig.xhss / 1000, 1)
                manualPp = Math.round(sig.xpss)
            }
            clampToMinimums()
            setManualSignature()
        }
        if (!isManual()) {
            focusPower.updateFocusPower($plan.targetAthlete)
            $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
        }
        updateGoalTargets()
    }

    function setManualSignature() {
        const positive = (v: number | undefined): v is number => v !== undefined && Number.isFinite(v) && v > 0
        // Peak power must be above threshold power for a valid signature
        if (!positive(manualFtp) || !positive(manualHie) || !positive(manualPp) || manualPp <= manualFtp) return false
        $plan.manualSignature = { ftp: manualFtp, hie: manualHie * 1000, pp: manualPp }
        return true
    }

    // While typing, wait for values at or above the minimum (e.g. "3" on the way to "350")
    function onManualSignatureInput() {
        const min = getMinValues(minSignature)
        if ((manualFtp ?? 0) < min.ftp || (manualHie ?? 0) < min.hie || (manualPp ?? 0) < min.pp) return
        if (setManualSignature()) updateGoalTargets()
    }

    // On commit (blur, enter, spinner), raise values below the minimum
    function onManualSignatureChange() {
        clampToMinimums()
        if (setManualSignature()) updateGoalTargets()
    }

    /* Achievability Module*/
    export const programAchievability  = createProgramAchievability(ProgramType.Goal)
    const {overallAchievability, hoursAchievability, statusMessage, targetAchievability} = programAchievability
    function validateTargets() {
        const startDate = new Date(initialInputs.t0*1000)
        // A goal by focus against its estimated frontier, in the slider's whole watts as on the wizard: fine up to 90% of
        // it, a warning up to it, an error past it (see assessCapacity in optimizer.cpp). Until it is known, and for
        // manual targets, against the typical ramp rates.
        if (!isManual() && frontier != null) {
            const frontierWatts = Math.round(frontier)
            const status = focusIncrease <= Math.floor(0.9*frontier) ? "fine" : focusIncrease <= frontierWatts ? "warning" : "error"
            const later = assessedFocusPower === $plan.focusPower && earliestDate ? format(earliestDate, "d MMM yyyy") : null
            const message = status === "fine"
                ? `+${focusIncrease}w is within your Estimated Frontier of ${signed(frontierWatts)}w. Forecast AI will likely be able to generate a suitable plan.`
                : status === "warning"
                ? `+${focusIncrease}w is close to your Estimated Frontier of ${signed(frontierWatts)}w, so your plan may fall a few watts short of it.` + (later ? ` A target date of ${later} would give it more room.` : "")
                : `+${focusIncrease}w is past your Estimated Frontier of ${signed(frontierWatts)}w. Plans level off at about the frontier, so yours may fall a watt or so short of it by ${format($plan.targetDate, "d MMM yyyy")}, though it may still reach it.`
                    + (later ? ` A target date of ${later} would put it within reach.` : "") + " You can also choose a smaller increase or add riding time to your availability."
            return programAchievability.validateFocusGoal($plan.targetDate, startDate, targetTL, currentTL, $plan.settings.maxWeeklyHours, status, message)
        }
        // Manual signature targets by each signature value's status against the training capacity: fine, a warning when the
        // plan may fall a little short of it, an error when it is unlikely to reach it
        if (isManual() && manualAssessment) {
            const toAchievability = (status: GoalStatus) => status === "fine" ? "fine" : status === "approaching" ? "warning" : "error"
            const systems = {
                low: toAchievability(manualAssessment.systems.low),
                high: toAchievability(manualAssessment.systems.high),
                peak: toAchievability(manualAssessment.systems.peak),
            } as const
            return programAchievability.validateFocusGoal($plan.targetDate, startDate, targetTL, currentTL, $plan.settings.maxWeeklyHours,
                systems, manualTargetsMessage(systems, manualAssessment.earliestDate))
        }
        const difficulty = !$plan.settings.difficultyFromTrainingLoad ? $plan.settings.difficulty : null
        return programAchievability.validateTargets($plan.targetDate, startDate, targetTL, currentTL, $plan.settings.maxWeeklyHours, difficulty)
    }

    // The achievability message for manual signature targets, naming the values short of fine: Threshold Power from the low
    // training load, High Intensity Energy from the high and Peak Power from the peak
    const signatureNames = { low: "Threshold Power", high: "High Intensity Energy", peak: "Peak Power" } as const
    function manualTargetsMessage(systems: Record<keyof typeof signatureNames, "fine" | "warning" | "error">, earliest: Date | null) {
        const by = format($plan.targetDate, "d MMM yyyy")
        const later = earliest ? format(earliest, "d MMM yyyy") : null
        const named = (status: string) => {
            const names = (Object.keys(signatureNames) as (keyof typeof signatureNames)[]).filter(k => systems[k] === status).map(k => signatureNames[k])
            const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]
            return { list, plural: names.length > 1 }
        }
        const errors = named("error"), warnings = named("warning")
        if (errors.list) {
            return `Your ${errors.list} target${errors.plural ? "s are" : " is"} beyond what your training can build by ${by}, so your plan is unlikely to reach ${errors.plural ? "them" : "it"}.`
                + (later ? ` A target date of ${later} would put your targets within reach.` : " No target date within a year reaches them with this availability.")
                + " You can also lower the targets or add riding time to your availability."
        }
        if (warnings.list) {
            return `Your ${warnings.list} target${warnings.plural ? "s are" : " is"} close to what your training can build by ${by}, so your plan may fall a little short of ${warnings.plural ? "them" : "it"}.`
                + (later ? ` A target date of ${later} would give ${warnings.plural ? "them" : "it"} more room.` : "")
        }
        return `Your target signature is within what your training can build by ${by}. Forecast AI will likely be able to generate a suitable plan.`
    }

    async function clearForecastInputs() {
        forecastInputs = null
        await tick() // await to ensure DOM updates
    }

    let initialInputs = null
    async function updateInitialInputs() {
        initialInputs = await initForecastInputs({
            start: $plan.targetDate,
            startDate: $plan.startDate,
            focus_power: $plan.focusPower,
            focus: $plan.targetAthlete,
            specificity_rating: $plan.specificity,
            mode: "target_focus",
        })
        // Fresh inputs can carry other starting loads or rides than the key covers (an advice refresh after a ride): the
        // frontier, or the manual targets' status, is assessed again
        frontierKey = ""

        updateGoalTargets()
    }

    async function updateGoalTargets() {
        if (isManual()) await setFocusFromTrainingLoads()
        if (isManual() && $plan.manualSignature) {
            // Focus power implied by the target signature, used to check the forecast reaches the goal
            const sig = $plan.manualSignature
            $plan.focusPower = Math.round((await getLibxert()).power_from_duration(sig.pp, sig.hie, sig.ftp, $userParams.athletes[$plan.targetAthlete].duration))
        }
        await clearForecastInputs()
        forecastInputs = getForecastInputs()
        updateFrontier(forecastInputs)
        if (!await updateTargets(forecastInputs)) return

        // The minimum changes with the dates, so re-check the manual values
        if (isManual() && clampToMinimums() && setManualSignature()) return updateGoalTargets()

        validateTargets()
    }

    function getForecastInputs() {
        return createForecastInputs<GoalForecastTargets>(
            {   
                program: ProgramType.Goal,
                focus_power: $plan.focusPower,
                focus: $plan.targetAthlete,
                specificity_rating: $plan.specificity,
                start: $plan.targetDate,
                currentFocusPower: $currentFocusPower,
                manualSignature: isManual() ? $plan.manualSignature : null,
            },
            $userParams.athletes,
            initialInputs,
            $plan.settings
        )
    }

    // Each estimate supersedes the ones before it: false when a later one has started, so a slower earlier estimate
    // isn't applied last.
    let targetsRequest = 0
    async function updateTargets(inputs: any) {
        const request = ++targetsRequest
        const targets = await estimateTargets(inputs, $plan.cal_settings, $trainingMedians)
        if (request !== targetsRequest) return false
        targetTL = targets.targetTL
        targetSignature = targets.targetSignature
        minSignature = targets.minSignature
        return true
    }

    /**
     * When changing settings or availability validation needs to be rerun, and a Focus Goal's estimated targets depend
     * on them.
     */
     async function refreshForecastInputs() {
        await clearForecastInputs()
        forecastInputs = getForecastInputs()
        updateFrontier(forecastInputs)
        if (!await updateTargets(forecastInputs)) return
        validateTargets()
    }

    // A change to the week's availability (WeekAvailability) is assessed again shortly after
    let availabilityJson = JSON.stringify($plan.cal_settings?.defaults)
    let availabilityTimer: ReturnType<typeof setTimeout>
    $: {
        const json = JSON.stringify($plan.cal_settings?.defaults)
        if (json !== availabilityJson) {
            availabilityJson = json
            clearTimeout(availabilityTimer)
            availabilityTimer = setTimeout(refreshForecastInputs, 400)
        }
    }

    // Adjusting the frontier (FrontierAdjustDialog), as on the wizard (FirstTimeGoalForm): the frontier a target date and
    // availability reach, with the increase at the fine share of the wanted frontier, so that the earliest date the goal is
    // fine is the earliest the frontier is reached. The forecast inputs for a date are fetched once each time it opens.
    const FINE_SHARE = 0.9
    let frontierOpen = false
    let frontierInputs = new Map<string, Promise<any>>()
    function openFrontier() {
        frontierInputs = new Map()
        frontierOpen = true
    }
    async function assessFrontier(date: string, defaults: CalendarDefaults, wanted: number, maxWeeklyHours: number) {
        const start = LocalDate.atNoon(date)
        const focus_power = $currentFocusPower + FINE_SHARE*wanted
        const key = `${date} ${$plan.targetAthlete} ${$plan.specificity} ${Math.round(focus_power)}`
        if (!frontierInputs.has(key)) {
            frontierInputs.set(key, initForecastInputs({ start, startDate: $plan.startDate, focus_power: Math.round(focus_power), focus: $plan.targetAthlete, specificity_rating: $plan.specificity, mode: "target_focus" }))
        }
        const inputs = await frontierInputs.get(key)
        const forecastInputs = createForecastInputs<GoalForecastTargets>(
            { program: ProgramType.Goal, focus_power, focus: $plan.targetAthlete, specificity_rating: $plan.specificity, start, currentFocusPower: $currentFocusPower, manualSignature: null },
            $userParams.athletes,
            structuredClone(inputs),
            { ...$plan.settings, maxWeeklyHours }
        )
        const result = await assessGoal(forecastInputs, { ...$plan.cal_settings, defaults }, $trainingMedians)
        return result && { frontier: result.frontierFocusPower - result.currentFocusPower, earliestDate: result.earliestDate }
    }
    function applyFrontier(date: string, defaults: CalendarDefaults, maxWeeklyHours: number) {
        $plan.cal_settings.defaults = defaults
        const hoursChanged = maxWeeklyHours !== $plan.settings.maxWeeklyHours
        if (hoursChanged) $plan.settings.maxWeeklyHours = maxWeeklyHours
        if (date !== targetDate) {
            targetDate = date
            $plan.targetDate = LocalDate.atNoon(targetDate)
            max_focusIncrease = getMaxFocusIncrease()
            updateInitialInputs()
        } else if (hoursChanged) {
            // Nothing else assesses a new Max Weekly Hours: the availability watcher only sees the days
            refreshForecastInputs()
        }
    }

</script>

{#if frontierWatts != null && !disabled}
    <FrontierAdjustDialog bind:open={frontierOpen} frontier={frontierWatts} {targetDate} defaults={$plan.cal_settings.defaults} maxMonths={12}
        maxWeeklyHours={$plan.settings.maxWeeklyHours}
        trainingMedians={$trainingMedians} assess={assessFrontier} onApply={applyFrontier}/>
{/if}

<div class={className}>
    <div class="flex flex-col gap-5 flex-1 md:flex-row">
        <div class="basis-1/2 grid grid-cols-[2fr,5fr] auto-rows-max items-start gap-y-8 gap-x-3 md:gap-x-6 text-left text-base md:grid">

            <slot name="adapt-choice"/>
            
            {#if !hideStartDate}
            <span class="text-slate-600">Start Date</span>
            <DatePicker class="w-52 text-base" min={LocalDate.format(new Date())} max={LocalDate.format(addDays($plan.targetDate, -1))} {disabled}
                bind:value={startDate}
                on:input={ () => {
                    $plan.startDate = startDateFromInput(startDate)
                    updateInitialInputs()
                }}
            />
            <FutureStartNote startDate={$plan.startDate} />
            {/if}

            <span class="text-slate-600">End Date</span>
            <DatePicker class="w-52 text-base" min={LocalDate.format($plan.startDate ? addDays($plan.startDate, 1) : new Date())} {disabled}
                bind:value={targetDate}
                on:input={ () => {
                    $plan.targetDate = LocalDate.atNoon(targetDate)
                    max_focusIncrease = getMaxFocusIncrease()
                    focusIncrease = Math.min(focusIncrease, focusIncreaseLimit(frontier, max_focusIncrease, isManual()))
                    $plan.focusPower = focusIncrease + $currentFocusPower
                    updateInitialInputs()
                }}
            />

            <span class="text-slate-600">Goal Targets</span>
            <RadioGroup.Root class="twp flex flex-wrap items-center gap-x-3 gap-y-2 text-left" {disabled}
                value={$plan.targetsMode}
                onValueChange={onTargetsModeValueChange}
            >
                <RadioGroup.Item value="focus" id="goal-targets-focus" />
                <Label class="font-normal text-base mb-0 mr-4" for="goal-targets-focus">By Focus</Label>
                <RadioGroup.Item value="manual" id="goal-targets-manual" />
                <Label class="font-normal text-base mb-0" for="goal-targets-manual">Manual Signature Targets</Label>
            </RadioGroup.Root>

            {#if $plan.targetsMode === "manual"}
            <span class="text-slate-600">Threshold Power</span>
            <div class="flex flex-col gap-1">
                <div class="flex items-baseline gap-2">
                    <input class="input-xfai w-32" type="number" min={minValues.ftp} step="1" {disabled} bind:value={manualFtp} on:input={onManualSignatureInput} on:change={onManualSignatureChange}/>
                    <span class="text-slate-500 text-sm">w</span>
                </div>
                {#if minNotes.ftp}
                <p transition:slide class="text-sm text-amber-700 text-left">Lowest possible is {minValues.ftp} w: your threshold power on the end date with no training until then.</p>
                {/if}
            </div>

            <span class="text-slate-600">High Intensity Energy</span>
            <div class="flex flex-col gap-1">
                <div class="flex items-baseline gap-2">
                    <input class="input-xfai w-32" type="number" min={minValues.hie} step="0.1" {disabled} bind:value={manualHie} on:input={onManualSignatureInput} on:change={onManualSignatureChange}/>
                    <span class="text-slate-500 text-sm">kJ</span>
                </div>
                {#if minNotes.hie}
                <p transition:slide class="text-sm text-amber-700 text-left">Lowest possible is {minValues.hie} kJ: your high intensity energy on the end date with no training until then.</p>
                {/if}
            </div>

            <span class="text-slate-600">Peak Power</span>
            <div class="flex flex-col gap-1">
                <div class="flex items-baseline gap-2">
                    <input class="input-xfai w-32" type="number" min={minValues.pp} step="1" {disabled} bind:value={manualPp} on:input={onManualSignatureInput} on:change={onManualSignatureChange}/>
                    <span class="text-slate-500 text-sm">w</span>
                </div>
                {#if minNotes.pp}
                <p transition:slide class="text-sm text-amber-700 text-left">Lowest possible is {minValues.pp} w: your peak power on the end date with no training until then.</p>
                {/if}
            </div>
            {:else}
            <!-- The focus, specificity and increase have rows of their own, wide enough for the boxes and the frontier -->
            <div class="col-span-2 flex flex-col gap-2 text-left">
                <span class="text-slate-600">Focus</span>
                <FocusPicker {athleteTypes} durations={athleteDurations} {disabled}
                    bind:targetAthlete={$plan.targetAthlete} bind:specificity={$plan.specificity}
                    signature={currentSignature ? { ftp: currentSignature.xlss, hie: currentSignature.xhss, pp: currentSignature.xpss } : null}
                    currentFocusPower={$currentFocusPower} focusPower={$plan.focusPower}
                    onFocusInput={() => {
                        focusPower.updateFocusPower($plan.targetAthlete)
                        $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
                        updateGoalTargets()
                    }}
                    onSpecificityInput={updateGoalTargets}
                />
            </div>

            <!-- The increase, in the slider's thumb, from Flexible (room to miss workouts) to Rigid at the frontier, its end, with
                 the last tenth before it in yellow -->
            <div class="col-span-2 flex flex-col gap-1 text-left">
                <span class="flex items-center gap-1 text-slate-600">
                    Power Increase
                    {#if frontierWatts != null && focusIncrease > frontierWatts}
                        <span transition:slide={{axis: "x"}}>
                            <HelpTooltip icon="fa-solid fa-circle-exclamation" iconClass="text-red-600 -my-1" label="About this increase"
                                message="This is past your Estimated Frontier. Plans level off at about the frontier, so yours may fall a watt or so short of this by the end date, though it may still reach it. Your plan won't allow you to miss any workouts."/>
                        </span>
                    {:else if frontierWatts != null && fineWatts != null && focusIncrease > fineWatts}
                        <span transition:slide={{axis: "x"}}>
                            <HelpTooltip icon="fa-solid fa-triangle-exclamation" iconClass="text-amber-500 -my-1" label="About this increase"
                                message="Your training plan may not allow you to miss any workouts without modifying your plan's configuration."/>
                        </span>
                    {/if}
                </span>
                <div class="flex items-start gap-4">
                    <div class="flex-1 min-w-0">
                        <SelectSlider min={min_focusIncrease} max={sliderMax} step={1} thumbSize={38} filled warnFrom={frontierWatts != null && !noRoom ? fineWatts : null} errorFrom={noRoom ? frontierWatts : null} {disabled}
                            bind:value={focusIncrease}
                            on:input={() => {
                                increaseFromFrontier = false
                                $plan.focusPower = Math.round($currentFocusPower + focusIncrease)
                                updateGoalTargets()
                            }}
                            aria-label="Power increase in watts"
                        >
                            <span slot="thumb" class="text-xs font-bold text-slate-800 tabular-nums leading-none">+{Math.round($plan.focusPower) - Math.round($currentFocusPower)}<span class="font-medium text-slate-500">w</span></span>
                        </SelectSlider>
                        {#if noRoom && frontierWatts != null}
                            <p class="pt-2 text-xs text-slate-500">
                                {frontierWatts < 0 ? `This end date, availability and Max Weekly Hours can't hold your power: it may fall ${-frontierWatts}w by the end date.` : "This end date, availability and Max Weekly Hours leave no room for an increase."}
                                Adjust them to find what reaches one.
                            </p>
                        {:else if frontierWatts != null}
                            <FrontierEnds min={min_focusIncrease} max={sliderMax} frontier={frontierWatts} thumbSize={38}/>
                        {/if}
                    </div>
                    {#if frontierWatts != null}
                        <!-- The frontier, where the slider ends, and its adjustment -->
                        <div class="flex flex-col items-end">
                            <div class="flex items-center gap-2">
                                <span class="font-semibold tabular-nums">{signed(frontierWatts)}<span class="text-slate-500 text-sm font-normal">w</span></span>
                                <button type="button" class="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 hover:border-xerteal-400 hover:text-xerteal-700 disabled:opacity-60"
                                    {disabled} on:click={openFrontier}>
                                    <i class="fa-regular fa-wrench text-[10px]"/> Adjust
                                </button>
                            </div>
                            <FrontierLabel withDifficulty/>
                        </div>
                    {/if}
                </div>
            </div>
            {/if}

            <!-- The week's availability, as on the wizard: a day opens its availability to change it -->
            <div class="col-span-2 flex flex-col gap-2 text-left">
                <div class="flex items-baseline">
                    <span class="text-slate-600 mr-auto">Availability</span>
                    <span class="text-xs text-slate-400">Tap a day to change it</span>
                </div>
                <WeekAvailability {disabled} bind:defaults={$plan.cal_settings.defaults} trainingMedians={$trainingMedians}/>
                <span class="text-slate-400 italic text-sm">*Changes will not apply globally until the program is saved.</span>
            </div>

            <div class="col-span-2 flex flex-col gap-1 text-left">
                <span class="text-slate-600">Max Weekly Hours</span>
                <WeeklyHoursSlider {disabled} hoursAchievability={$hoursAchievability.status}
                    bind:hours={$plan.settings.maxWeeklyHours}
                    onInput={refreshForecastInputs}
                />
            </div>

            <XfaiGeneralSettings class="col-span-2" bind:settings={$plan.settings} {disabled} onInput={refreshForecastInputs}/>
            
        </div>

        <div class="h-px md:w-px md:h-auto bg-black/10"/>

        <div class="basis-1/2 flex flex-col gap-10 text-base">
            {#if $plan.targetsMode === "manual" && $plan.manualSignature}
            <!-- Same layout as FocusPowerIncrease -->
            <div class="flex flex-wrap gap-1 text-base text-left items-baseline leading-none">
                <span>Reach a target Fitness Signature of</span>
                <div>
                    <span class="font-bold text-2xl mx-1 leading-none tracking-tight">{Math.round($plan.manualSignature.ftp)}<span class="text-slate-500 font-normal text-base">w</span></span>
                    <span class="text-slate-600">TP,</span>
                    <span class="font-bold text-2xl mx-1 leading-none tracking-tight">{round($plan.manualSignature.hie / 1000, 1)}<span class="text-slate-500 font-normal text-base">kJ</span></span>
                    <span class="text-slate-600">HIE and</span>
                    <span class="font-bold text-2xl mx-1 leading-none tracking-tight">{Math.round($plan.manualSignature.pp)}<span class="text-slate-500 font-normal text-base">w</span></span>
                    <span class="text-slate-600">PP by </span>
                    <span class="font-bold text-lg ml-1 leading-none tracking-tight">{format($plan.targetDate, "LLLL dd, yyyy")}</span>.
                </div>
            </div>
            {:else}
            <FocusPowerIncrease 
                currentFocusPower={$currentFocusPower} projectedFocusPower={$plan.focusPower}
                date={$plan.targetDate} focusPowerDuration={$focusPowerDuration}
            />
            {/if}

            <div id={achievabilityId} class="flex flex-col gap-3 text-left">
                <span class="font-bold">
                    Achievability
                    <InfoIcon href={'http://baronbiosys.com/glossary/achievability-goal'} />
                </span>
                <ProgramAchievabilityMessage statusMessage={$statusMessage} status={$overallAchievability}/>
            </div>

            <ProgramFitnessSignature 
                targetAchievability={$targetAchievability} {currentSignature} {targetSignature}
                description = "Required to achieve the goal"
        
            />
        
            <ProgramTrainingLoad 
                targetAchievability={$targetAchievability} {currentTL} {targetTL}
                description = "Achieved on completion of required fitness signature"
            />

        </div>
    </div>
</div>